import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { ONBOARDING_CUTOFF_DATE } from "@/hooks/useOnboardingStatus";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

export default function ProtectedRoute({ children, requireAdmin = false }: ProtectedRouteProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Pages that should be accessible during onboarding
  const onboardingAllowedPaths = ["/onboarding", "/client/onboarding"];
  const isOnboardingPage = onboardingAllowedPaths.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (import.meta.env.DEV) console.log('ProtectedRoute: User check', { user: user?.id, requireAdmin });
      setUser(user);

      if (user) {
        // Check admin role first
        const { data: adminRole } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .maybeSingle();
        
        const hasAdminRole = !!adminRole;
        setIsAdmin(hasAdminRole);

        // If admin or admin-required route, skip client checks
        if (hasAdminRole || requireAdmin) {
          setLoading(false);
          return;
        }


        // Check client status (for non-admin routes)
        const { data: clientData } = await supabase
          .from('clients')
          .select('id, archived, approved, onboarding_status, created_at')
          .eq('user_id', user.id)
          .maybeSingle();

        if (clientData?.archived) {
          if (import.meta.env.DEV) console.log('ProtectedRoute: Client is archived');
          setIsArchived(true);
          setLoading(false);
          return;
        }

        // Check if client needs onboarding (non-grandfathered clients)
        if (clientData && !isOnboardingPage) {
          const clientCreatedAt = new Date(clientData.created_at);
          const cutoffDate = new Date(ONBOARDING_CUTOFF_DATE);
          
          const isGrandfathered = 
            clientCreatedAt < cutoffDate ||
            clientData.approved === true ||
            clientData.onboarding_status === "grandfathered" ||
            clientData.onboarding_status === "completed";

          const shouldOnboard = 
            !isGrandfathered && 
            (clientData.onboarding_status === "not_started" || 
             clientData.onboarding_status === "in_progress" ||
             !clientData.onboarding_status);

          setNeedsOnboarding(shouldOnboard);
        }
      }
      
      setLoading(false);
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (import.meta.env.DEV) console.log('ProtectedRoute: Auth state change', { event, user: session?.user?.id });
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, [requireAdmin, location.pathname]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show archived message for archived clients
  if (isArchived && !requireAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <CardTitle>Account Archived</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Your account has been archived. If you believe this is an error, please contact support.
            </p>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Email: <a href="mailto:contact@blackdiamondscg.com" className="text-primary hover:underline">contact@blackdiamondscg.com</a>
              </p>
              <Button
                variant="outline"
                onClick={async () => {
                  await supabase.auth.signOut();
                  window.location.href = '/login';
                }}
              >
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (requireAdmin && !isAdmin) {
    if (import.meta.env.DEV) console.log('ProtectedRoute: Redirecting to client dashboard - no admin role');
    return <Navigate to="/client/dashboard" replace />;
  }

  // Redirect admins away from client-only routes to the admin dashboard
  if (isAdmin && !requireAdmin && location.pathname.startsWith('/client')) {
    if (import.meta.env.DEV) console.log('ProtectedRoute: Redirecting admin away from client route');
    return <Navigate to="/admin/dashboard" replace />;
  }

  // Redirect to onboarding if needed (non-admin clients who haven't completed onboarding)
  if (needsOnboarding && !isOnboardingPage && !requireAdmin && !isAdmin) {
    if (import.meta.env.DEV) console.log('ProtectedRoute: Redirecting to onboarding');
    return <Navigate to="/onboarding" replace />;
  }

  if (import.meta.env.DEV) console.log('ProtectedRoute: Allowing access', { requireAdmin, isAdmin });
  return <>{children}</>;
}
